/* GPROPERTY-SPATIAL-AREA-UI-01
 * Map-first, local-only sketch preview attached to canonical GProperty Map.
 * No customer data persistence, API calls, credit charges, measured survey inference,
 * cadastral claims, professional PM01/BA01 execution, or new map instance.
 */
(function (window, document) {
  'use strict';
  var ROOT = '[data-gproperty-map-workspace][data-product="gproperty"]';
  var OWNER = '[data-gproperty-spatial-tool]';
  var active = new WeakSet();
  var MESSAGES = {
    en: {
      idle: 'Mark at least three corners directly on the map.',
      drawing: 'Tap map corners, or add the current map centre for keyboard access.',
      few: 'Select at least three corners, then choose Finish.',
      done: 'Map sketch ready. Approximation only; not a measured boundary.',
      cleared: 'Sketch cleared. Start again to draw a new area.',
      unavailable: 'The sketch tool is unavailable without the map.',
      NOT_ENOUGH_POINTS: 'Select at least three corners.',
      TOO_MANY_POINTS: 'Maximum 64 corners. Finish or clear the sketch.',
      INVALID_VERTEX: 'This point is not a valid map coordinate.',
      DATELINE_UNSUPPORTED: 'This sketch cannot cross the date line.',
      DUPLICATE_VERTEX: 'Two corners are too close. Undo and retry.',
      TOO_LARGE_FOR_SKETCH: 'Sketches must be within 5 km. Zoom in on one site.',
      BOUNDARY_SELF_INTERSECTS: 'The boundary crosses itself. Undo and redraw.',
      DEGENERATE_AREA: 'The selected area is too small or has no usable surface.',
    },
    zh: {
      idle: '在地圖上選取至少三個角點，即可估算範圍。',
      drawing: '點擊地圖加角點，或使用「加中心點」進行鍵盤操作。',
      few: '請至少選取三個角點，再點擊完成。',
      done: '地圖草圖已完成，僅屬概算，不代表實測邊界。',
      cleared: '已清除草圖，可重新開始標記。',
      unavailable: '地圖尚未可用，不能進行範圍預覽。',
      NOT_ENOUGH_POINTS: '請至少選取三個角點。',
      TOO_MANY_POINTS: '最多 64 個角點，請完成或清除草圖。',
      INVALID_VERTEX: '角點坐標無效。',
      DATELINE_UNSUPPORTED: '不支援跨越國際換日線的草圖。',
      DUPLICATE_VERTEX: '兩個角點距離過近，請撤銷後重試。',
      TOO_LARGE_FOR_SKETCH: '範圍不能超過 5 公里，請放大至單一地塊。',
      BOUNDARY_SELF_INTERSECTS: '邊界線相交，請撤銷並重新標記。',
      DEGENERATE_AREA: '範圍過小或無法形成有效面積。',
    },
  };

  function find(parent, selector) {
    return parent.querySelector(selector);
  }
  function show(element, visible) {
    if (element) element.hidden = !visible;
  }
  function number(value, language) {
    return new Intl.NumberFormat(language === 'zh' ? 'zh-HK' : 'en-HK', {
      maximumFractionDigits: 1,
      minimumFractionDigits: 0,
    }).format(value);
  }

  function mount(workspace) {
    if (!workspace || !workspace.matches(ROOT) || active.has(workspace)) return false;
    var owner = find(workspace, OWNER);
    var map = workspace.__map;
    if (
      !owner ||
      !map ||
      workspace.__mapRenderer === 'mapbox-gl' ||
      !window.L ||
      !window.GPropertySpatialAreaMath
    )
      return false;
    var math = window.GPropertySpatialAreaMath;
    var language = owner.getAttribute('data-spatial-language') === 'en' ? 'en' : 'zh';
    var words = MESSAGES[language];
    var toggle = find(owner, '[data-spatial-toggle]');
    var panel = find(owner, '[data-spatial-panel]');
    var start = find(owner, '[data-spatial-start]');
    var centre = find(owner, '[data-spatial-centre]');
    var undo = find(owner, '[data-spatial-undo]');
    var finish = find(owner, '[data-spatial-finish]');
    var clear = find(owner, '[data-spatial-clear]');
    var status = find(owner, '[data-spatial-status]');
    var result = find(owner, '[data-spatial-result]');
    var area = find(owner, '[data-spatial-area]');
    var perimeter = find(owner, '[data-spatial-perimeter]');
    if (
      ![toggle, panel, start, centre, undo, finish, clear, status, result, area, perimeter].every(
        Boolean
      )
    )
      return false;

    var points = [];
    var drawing = false;
    var shape = null;
    var corners = [];

    function message(key) {
      status.textContent = words[key] || words.unavailable;
    }
    function updateControls() {
      start.disabled = drawing;
      centre.disabled = !drawing || points.length >= math.MAX_POINTS;
      undo.disabled = !drawing || points.length === 0;
      finish.disabled = !drawing || points.length < 3;
      clear.disabled = !drawing && points.length === 0;
      toggle.setAttribute('aria-expanded', String(!panel.hidden));
      workspace.dataset.spatialDrawing = drawing ? 'true' : 'false';
      var canvas = map.getContainer();
      if (canvas) canvas.classList.toggle('is-spatial-drawing', drawing);
    }
    function clearLayers() {
      if (shape) {
        map.removeLayer(shape);
        shape = null;
      }
      corners.forEach(function (marker) {
        map.removeLayer(marker);
      });
      corners.length = 0;
    }
    function renderOutline() {
      clearLayers();
      if (!points.length) return;
      if (points.length >= 3) {
        shape = window.L.polygon(points, {
          color: '#256d67',
          fillColor: '#55b7ab',
          fillOpacity: 0.18,
          weight: 2,
          interactive: false,
        }).addTo(map);
      } else if (points.length >= 2) {
        shape = window.L.polyline(points, {
          color: '#256d67',
          dashArray: '5 5',
          weight: 2,
          interactive: false,
        }).addTo(map);
      }
      points.forEach(function (p) {
        corners.push(
          window.L.circleMarker(p, {
            radius: 4,
            color: '#18554d',
            fillColor: '#fff',
            fillOpacity: 1,
            weight: 2,
            interactive: false,
          }).addTo(map)
        );
      });
    }
    function reset() {
      drawing = false;
      points = [];
      clearLayers();
      show(result, false);
      owner.dataset.sketchStatus = 'empty';
      message('cleared');
      updateControls();
    }
    function addPoint(p) {
      if (!drawing || !p) return;
      if (points.length >= math.MAX_POINTS) {
        message('TOO_MANY_POINTS');
        return;
      }
      var lat = Number(p.lat),
        lng = Number(p.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        message('INVALID_VERTEX');
        return;
      }
      points.push({ lat: lat, lng: lng });
      show(result, false);
      renderOutline();
      message('drawing');
      updateControls();
    }
    function startDrawing() {
      reset();
      drawing = true;
      message('drawing');
      updateControls();
      var canvas = map.getContainer();
      if (canvas && typeof canvas.focus === 'function') canvas.focus({ preventScroll: true });
    }
    function finishDrawing() {
      if (!drawing) return;
      try {
        var calculation = math.analyze(points);
        area.textContent = number(calculation.area_m2, language) + ' m²';
        perimeter.textContent = number(calculation.perimeter_m, language) + ' m';
        drawing = false;
        message('done');
        show(result, true);
        updateControls();
        // Never emit 'calculation verified' or mutate a customer/product state.
        owner.dataset.sketchStatus = 'local_approximate_only';
      } catch (error) {
        message(error && error.code);
      }
    }
    toggle.addEventListener('click', function () {
      show(panel, panel.hidden);
      if (panel.hidden && drawing) reset();
      updateControls();
    });
    start.addEventListener('click', startDrawing);
    centre.addEventListener('click', function () {
      addPoint(map.getCenter());
    });
    undo.addEventListener('click', function () {
      if (!drawing || !points.length) return;
      points.pop();
      show(result, false);
      renderOutline();
      message('drawing');
      updateControls();
    });
    finish.addEventListener('click', finishDrawing);
    clear.addEventListener('click', reset);
    map.on('click', function (event) {
      if (!drawing || !event || !event.latlng) return;
      addPoint(event.latlng);
    });
    map.getContainer().addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && drawing) {
        reset();
        toggle.focus();
      }
    });
    active.add(workspace);
    message('idle');
    updateControls();
    // Explicit LittleG Calculators navigation may expand the FREE sketch panel.
    // Do not start drawing or invoke professional PM01 automatically.
    if (
      window.location &&
      /(?:\?|&)littleg_tool=area(?:&|$)/.test(String(window.location.search || ''))
    ) {
      show(panel, true);
      updateControls();
      owner.dataset.sketchEntry = 'littleg_tools';
    }
    return true;
  }
  function mountAvailable() {
    document.querySelectorAll(ROOT).forEach(mount);
  }
  document.addEventListener('gproperty:map:ready', function (event) {
    mount(event.target);
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAvailable);
  } else {
    mountAvailable();
  }
  window.GPropertySpatialAreaUI = Object.freeze({ mount: mount });
})(window, document);
